/**
 * FarmRoadSettingsPanel
 *
 * Panneau de configuration des paramètres de la ferme :
 * - Type de culture
 * - Objectif agricole
 * - Seuils critiques par indicateur
 *
 * Les paramètres sont stockés au niveau de la ferme (/farms/{farmId}/settings)
 * et partagés entre tous les utilisateurs ayant accès à cette ferme.
 */

import React, { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid
} from '@mui/material';
import {
  Settings,
  Save,
  RefreshCw,
  ChevronDown,
  Thermometer,
  Droplet,
  Sun,
  Wind,
  Leaf,
  AlertCircle,
  CheckCircle,
  Info
} from 'lucide-react';

import FarmRoadThresholdsManager from './FarmRoadThresholdsManager';

const FarmRoadSettingsPanel = ({ farmId, farmName }) => {
  // États
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [message, setMessage] = useState(null);

  // Charger la configuration au montage
  useEffect(() => {
    loadConfiguration();
  }, [farmId]);

  const loadConfiguration = async () => {
    setLoading(true);
    try {
      const loadedConfig = await FarmRoadThresholdsManager.getThresholds(farmId);
      setConfig(loadedConfig);
    } catch (error) {
      console.error('Erreur chargement configuration:', error);
      setMessage({ type: 'error', text: 'Erreur lors du chargement de la configuration' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);

    try {
      console.log('[FarmRoadSettingsPanel] Sauvegarde config:', config);
      const success = await FarmRoadThresholdsManager.saveThresholds(farmId, config);

      if (success) {
        setMessage({ type: 'success', text: 'Paramètres enregistrés avec succès !' });
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: 'Erreur lors de l\'enregistrement' });
      }
    } catch (error) {
      console.error('Erreur sauvegarde:', error);
      setMessage({ type: 'error', text: 'Erreur lors de l\'enregistrement' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Réinitialiser aux valeurs par défaut pour cette culture ?')) {
      return;
    }

    setSaving(true);
    try {
      const success = await FarmRoadThresholdsManager.resetToDefaults(farmId, config.culture);

      if (success) {
        await loadConfiguration();
        setMessage({ type: 'success', text: 'Paramètres réinitialisés !' });
        setTimeout(() => setMessage(null), 3000);
      }
    } catch (error) {
      console.error('Erreur réinitialisation:', error);
      setMessage({ type: 'error', text: 'Erreur lors de la réinitialisation' });
    } finally {
      setSaving(false);
    }
  };

  const updateThreshold = (indicatorKey, field, value) => {
    setConfig(prev => ({
      ...prev,
      thresholds: {
        ...prev.thresholds,
        [indicatorKey]: {
          ...prev.thresholds[indicatorKey],
          [field]: parseFloat(value)
        }
      }
    }));
  };

  const cultures = FarmRoadThresholdsManager.getAvailableCultures();

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <CircularProgress />
      </div>
    );
  }

  if (!config) {
    return (
      <Alert severity="error">
        Impossible de charger la configuration
      </Alert>
    );
  }

  const indicatorIcons = {
    temperature: <Thermometer className="h-5 w-5 text-red-500" />,
    humidity: <Droplet className="h-5 w-5 text-blue-500" />,
    soil_moisture: <Droplet className="h-5 w-5 text-brown-500" />,
    light_intensity: <Sun className="h-5 w-5 text-yellow-500" />,
    co2: <Wind className="h-5 w-5 text-gray-500" />,
    daily_light_integral: <Sun className="h-5 w-5 text-orange-500" />,
    vpd_diurnal: <Wind className="h-5 w-5 text-teal-500" />,
    vpd: <Wind className="h-5 w-5 text-cyan-500" />,
    vpd_nocturnal: <Wind className="h-5 w-5 text-indigo-500" />,
    dew_point: <Droplet className="h-5 w-5 text-purple-500" />,
    sunlight_duration: <Sun className="h-5 w-5 text-amber-500" />,
    radiation_mean_diurnal: <Sun className="h-5 w-5 text-yellow-400" />
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* En-tête */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Settings className="h-6 w-6 text-blue-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Paramètres de la ferme</h2>
            <p className="text-sm text-gray-600">
              {farmName || `Ferme ${farmId}`} • Configuration partagée
            </p>
          </div>
        </div>

        {config.isDefault && (
          <Alert severity="info" className="mt-4">
            <div className="flex items-center gap-2">
              <Info className="h-5 w-5" />
              <span>Vous utilisez actuellement les valeurs par défaut. Configurez les seuils pour votre exploitation.</span>
            </div>
          </Alert>
        )}
      </div>

      {/* Messages */}
      {message && (
        <Alert severity={message.type} className="mb-4" onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Configuration générale */}
      <Card className="mb-4">
        <CardContent>
          <Typography variant="h6" className="mb-4 font-semibold flex items-center gap-2">
            <Leaf className="h-5 w-5 text-green-600" />
            Informations générales
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Type de culture</InputLabel>
                <Select
                  value={config.culture}
                  onChange={(e) => setConfig({ ...config, culture: e.target.value })}
                  label="Type de culture"
                >
                  {cultures.map((cult) => (
                    <MenuItem key={cult.id} value={cult.id}>
                      {cult.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {config.culture && (
                <Typography variant="caption" color="text.secondary" className="mt-2 block">
                  Coefficient cultural (Kc): {FarmRoadThresholdsManager.getCropCoefficient(config.culture)}
                </Typography>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Objectif agricole"
                value={config.objective || ''}
                onChange={(e) => setConfig({ ...config, objective: e.target.value })}
                placeholder="Ex: Maximiser le rendement, Production bio..."
              />
            </Grid>

            <Grid item xs={12}>
              <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <input
                  type="checkbox"
                  id="isCoveredCrop"
                  checked={config.isCoveredCrop !== false}
                  onChange={(e) => setConfig({ ...config, isCoveredCrop: e.target.checked })}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="isCoveredCrop" className="flex items-center gap-2 cursor-pointer">
                  <span className="text-2xl">🏠</span>
                  <div>
                    <Typography variant="body1" className="font-semibold text-gray-900">
                      Culture sous serre ou tunnel
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Les précipitations ne seront pas déduites des recommandations d'irrigation
                    </Typography>
                  </div>
                </label>
              </div>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Seuils critiques */}
      <Card>
        <CardContent>
          <Typography variant="h6" className="mb-4 font-semibold flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-orange-600" />
            Seuils critiques par indicateur
          </Typography>

          <Typography variant="body2" color="text.secondary" className="mb-4">
            Définissez les valeurs optimales et critiques pour chaque indicateur. Les alertes seront générées selon ces seuils.
          </Typography>

          {config.thresholds && Object.entries(config.thresholds).map(([key, threshold]) => (
            <Accordion key={key} className="mb-2">
              <AccordionSummary expandIcon={<ChevronDown />}>
                <div className="flex items-center gap-3 w-full">
                  {indicatorIcons[key] || <Settings className="h-5 w-5" />}
                  <div className="flex-1">
                    <Typography variant="subtitle1" className="font-semibold">
                      {key.replace(/_/g, ' ').toUpperCase()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {threshold.description}
                    </Typography>
                  </div>
                  <Chip
                    label={`${threshold.optimal_min} - ${threshold.optimal_max} ${threshold.unit}`}
                    color="success"
                    size="small"
                  />
                </div>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={2}>
                    <TextField
                      label="Critique min"
                      type="number"
                      size="small"
                      fullWidth
                      value={threshold.min_critical}
                      onChange={(e) => updateThreshold(key, 'min_critical', e.target.value)}
                      InputProps={{ endAdornment: threshold.unit }}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      label="Avert. min"
                      type="number"
                      size="small"
                      fullWidth
                      value={threshold.min_warning}
                      onChange={(e) => updateThreshold(key, 'min_warning', e.target.value)}
                      InputProps={{ endAdornment: threshold.unit }}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      label="Optimal min"
                      type="number"
                      size="small"
                      fullWidth
                      value={threshold.optimal_min}
                      onChange={(e) => updateThreshold(key, 'optimal_min', e.target.value)}
                      InputProps={{ endAdornment: threshold.unit }}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      label="Optimal max"
                      type="number"
                      size="small"
                      fullWidth
                      value={threshold.optimal_max}
                      onChange={(e) => updateThreshold(key, 'optimal_max', e.target.value)}
                      InputProps={{ endAdornment: threshold.unit }}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      label="Avert. max"
                      type="number"
                      size="small"
                      fullWidth
                      value={threshold.max_warning}
                      onChange={(e) => updateThreshold(key, 'max_warning', e.target.value)}
                      InputProps={{ endAdornment: threshold.unit }}
                    />
                  </Grid>
                  <Grid item xs={6} md={2}>
                    <TextField
                      label="Critique max"
                      type="number"
                      size="small"
                      fullWidth
                      value={threshold.max_critical}
                      onChange={(e) => updateThreshold(key, 'max_critical', e.target.value)}
                      InputProps={{ endAdornment: threshold.unit }}
                    />
                  </Grid>
                </Grid>

                <div className="mt-3 p-3 bg-gray-50 rounded">
                  <Typography variant="caption" color="text.secondary">
                    🔴 Critique : Intervention urgente requise •
                    ⚠️ Avertissement : Surveillance accrue •
                    ✅ Optimal : Conditions idéales
                  </Typography>
                </div>
              </AccordionDetails>
            </Accordion>
          ))}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-between items-center mt-6">
        <Button
          variant="outlined"
          onClick={handleReset}
          disabled={saving}
          startIcon={<RefreshCw className="h-4 w-4" />}
          color="warning"
        >
          Réinitialiser
        </Button>

        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : <Save className="h-4 w-4" />}
          color="primary"
          size="large"
        >
          {saving ? 'Enregistrement...' : 'Enregistrer les paramètres'}
        </Button>
      </div>

      {/* Info */}
      <Alert severity="info" className="mt-6">
        <Typography variant="caption">
          <strong>Note :</strong> Ces paramètres sont partagés avec tous les utilisateurs ayant accès à cette ferme.
          Les modifications seront appliquées immédiatement pour toutes les analyses futures.
        </Typography>
      </Alert>
    </div>
  );
};

export default FarmRoadSettingsPanel;
